import React, { useCallback, useContext, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslation } from "react-i18next";
import { IoCloseOutline, IoDocumentTextOutline, IoPrintOutline, IoReceiptOutline } from "react-icons/io5";
import MainContext from "src/context/MainContext";
import {
  FlowAccountDocument,
  FlowAccountDocumentsResult,
  FlowAccountSale,
  issueSaleDocuments,
  openDocumentPdf,
} from "src/api/FlowAccount";
import FullTaxInvoiceForm from "./FullTaxInvoiceForm";

/**
 * Thai tax documents for one sale, issued in FlowAccount.
 *
 * Opening it issues any missing abbreviated tax invoice first (idempotent on
 * the backend), so staff can print straight after taking the payment. Each
 * document prints as FlowAccount's PDF (TH or EN). A same-day abbreviated one
 * can be replaced by a full tax invoice for a company buyer.
 *
 * Visual language follows the booking "Print" modal in PaymentsRow.tsx.
 */

const INK = "#22356D";

const STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  sent: { bg: "#F0FDF4", fg: "#16A34A" },
  replaced: { bg: "#F1F5F9", fg: "#64748b" },
  voided: { bg: "#FEF2F2", fg: "#DC2626" },
  reversal_due: { bg: "#FFF7ED", fg: "#EA580C" },
  reversed: { bg: "#F1F5F9", fg: "#64748b" },
  failed: { bg: "#FEF2F2", fg: "#DC2626" },
  needs_review: { bg: "#FFF7ED", fg: "#EA580C" },
  pending: { bg: "#EFF6FF", fg: "#2563EB" },
};

interface Props {
  open: boolean;
  onClose: () => void;
  locationId: number;
  sale: FlowAccountSale;
  currency?: string;
}

const TaxDocumentsDialog = ({ open, onClose, locationId, sale, currency }: Props) => {
  const { t } = useTranslation();
  const { setNotification } = useContext(MainContext);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<FlowAccountDocumentsResult | null>(null);
  const [error, setError] = useState<string>("");
  const [fullFor, setFullFor] = useState<number | null>(null);
  const [printing, setPrinting] = useState<string>("");

  const saleKey = JSON.stringify(sale);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setResult(await issueSaleDocuments(locationId, JSON.parse(saleKey)));
    } catch (e: any) {
      setError(typeof e === "string" ? e : t("flowaccount.errorGeneric"));
    } finally {
      setLoading(false);
    }
  }, [locationId, saleKey, t]);

  useEffect(() => {
    if (open) {
      setFullFor(null);
      load();
    }
  }, [open, load]);

  const print = async (doc: FlowAccountDocument, culture: "th" | "en") => {
    setPrinting(`${doc.id}-${culture}`);
    try {
      await openDocumentPdf(locationId, doc.id, culture);
    } catch (e: any) {
      setNotification({ isShow: true, message: typeof e === "string" ? e : t("flowaccount.errorGeneric"), subMessage: "", type: "error" });
    } finally {
      setPrinting("");
    }
  };

  const docTitle = (doc: FlowAccountDocument) =>
    doc.kind === "full" ? t("flowaccount.fullTaxInvoice") : t("flowaccount.abbreviatedTaxInvoice");

  const docs = result?.documents || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{
        ".MuiDialog-paper": {
          width: { xs: "95%", sm: "460px" },
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 24px 48px rgba(0,0,0,0.12)",
        },
      }}
    >
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, py: 2.5, borderBottom: "1px solid #F1F5F9" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: "10px", background: "#F0F4FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <IoReceiptOutline size={18} color={INK} />
            </Box>
            <Box>
              <Typography sx={{ fontFamily: "var(--font-semibold)", fontSize: "16px", color: INK }}>
                {t("flowaccount.taxDocuments")}
              </Typography>
              <Typography sx={{ fontFamily: "var(--font-regular)", fontSize: "12px", color: "#94a3b8" }}>
                FlowAccount
              </Typography>
            </Box>
          </Box>
          <Box
            onClick={onClose}
            sx={{ width: 32, height: 32, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", "&:hover": { background: "#F1F5F9" } }}
          >
            <IoCloseOutline size={20} color="#64748b" />
          </Box>
        </Box>

        <Box sx={{ px: 3, py: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
          {loading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
              <CircularProgress size={18} />
              <Typography sx={{ fontFamily: "var(--font-regular)", fontSize: "13px", color: "#64748b" }}>
                {t("flowaccount.issuing")}
              </Typography>
            </Box>
          )}

          {!loading && error && (
            <Typography sx={{ fontFamily: "var(--font-regular)", fontSize: "13px", color: "#DC2626" }}>{error}</Typography>
          )}

          {!loading && result && !result.enabled && (
            <Box sx={{ p: 2, borderRadius: "10px", background: "#FFF7ED", border: "1px solid #FED7AA" }}>
              <Typography sx={{ fontFamily: "var(--font-semibold)", fontSize: "13px", color: "#9A3412" }}>
                {t("flowaccount.notActive")}
              </Typography>
              <Typography sx={{ fontFamily: "var(--font-regular)", fontSize: "12px", color: "#9A3412", mt: 0.5 }}>
                {result.blocked_reason}
              </Typography>
            </Box>
          )}

          {!loading && result && result.enabled && !docs.length && (
            <Typography sx={{ fontFamily: "var(--font-regular)", fontSize: "13px", color: "#64748b" }}>
              {result.issue_message || t("flowaccount.noDocuments")}
            </Typography>
          )}

          {!loading &&
            docs.map((doc) => {
              const colors = STATUS_COLORS[doc.status] || STATUS_COLORS.pending;
              return (
                <Box key={doc.id} sx={{ p: 2, borderRadius: "12px", border: "1px solid #E5E7EB" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        background: doc.kind === "full" ? "#EFF6FF" : "#FFF7ED",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {doc.kind === "full" ? (
                        <IoDocumentTextOutline size={20} color="#2563EB" />
                      ) : (
                        <IoReceiptOutline size={20} color="#EA580C" />
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontFamily: "var(--font-semibold)", fontSize: "14px", color: INK }}>
                        {docTitle(doc)}
                      </Typography>
                      <Typography sx={{ fontFamily: "var(--font-regular)", fontSize: "12px", color: "#94a3b8" }}>
                        {doc.document_serial || doc.reference}
                        {doc.amount != null ? ` · ${Number(doc.amount).toLocaleString()} ${currency || ""}` : ""}
                      </Typography>
                    </Box>
                    <Box sx={{ px: 1, py: 0.25, borderRadius: "6px", background: colors.bg }}>
                      <Typography sx={{ fontFamily: "var(--font-medium)", fontSize: "11px", color: colors.fg }}>
                        {t(`flowaccount.status.${doc.status}`)}
                      </Typography>
                    </Box>
                  </Box>

                  {doc.note && doc.status !== "sent" && (
                    <Typography sx={{ fontFamily: "var(--font-regular)", fontSize: "12px", color: "#64748b", mt: 1 }}>
                      {doc.note}
                    </Typography>
                  )}

                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
                    {doc.can_print &&
                      (["th", "en"] as const).map((culture) => (
                        <Box
                          key={culture}
                          onClick={() => !printing && print(doc, culture)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            px: 1.5,
                            py: 0.75,
                            borderRadius: "8px",
                            border: "1px solid #E5E7EB",
                            cursor: printing ? "wait" : "pointer",
                            "&:hover": { borderColor: INK, background: "#F8FAFF" },
                          }}
                        >
                          {printing === `${doc.id}-${culture}` ? <CircularProgress size={12} /> : <IoPrintOutline size={14} color={INK} />}
                          <Typography sx={{ fontFamily: "var(--font-medium)", fontSize: "12px", color: INK }}>
                            {culture === "th" ? t("flowaccount.printThai") : t("flowaccount.printEnglish")}
                          </Typography>
                        </Box>
                      ))}
                    {doc.can_issue_full && fullFor !== doc.id && (
                      <Box
                        onClick={() => setFullFor(doc.id)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          px: 1.5,
                          py: 0.75,
                          borderRadius: "8px",
                          background: INK,
                          cursor: "pointer",
                          "&:hover": { background: "#1a2956" },
                        }}
                      >
                        <IoDocumentTextOutline size={14} color="white" />
                        <Typography sx={{ fontFamily: "var(--font-medium)", fontSize: "12px", color: "white" }}>
                          {t("flowaccount.requestFull")}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {fullFor === doc.id && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #F1F5F9" }}>
                      <FullTaxInvoiceForm
                        locationId={locationId}
                        pushId={doc.id}
                        onCancel={() => setFullFor(null)}
                        onDone={(r) => {
                          setFullFor(null);
                          setNotification({
                            isShow: true,
                            message: `${t("flowaccount.fullIssued")} ${r.document_serial || ""}`.trim(),
                            subMessage: "",
                            type: "success",
                          });
                          load();
                        }}
                      />
                    </Box>
                  )}
                </Box>
              );
            })}
        </Box>
      </Box>
    </Dialog>
  );
};

export default TaxDocumentsDialog;
